import { forwardRef, useId, useMemo } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Radio,
  RadioGroup,
} from '@mui/material';

import BaseFieldContainer from '../common/baseFieldContainer';
import { hasRenderableHelperText } from '../utils/general';
import { FORM_CONTROL_LABEL_ROW_SX, FORM_CONTROL_LABEL_VERTICAL_SX } from './constants';
import {
  buildRadioGroupAriaIds,
  buildRadioGroupLayoutSx,
  buildInputLabelSx,
  getRadioGroupOutlinedBoxSx,
  mergeRadioGroupBorderSx,
} from './utils/general';

import type { FRadioGroupProps } from './types';

const FRadioGroup = forwardRef<HTMLDivElement, FRadioGroupProps>(function FRadioGroup(props, ref) {
  const {
    children,
    options,
    radioSize = 'small',
    label,
    helperText,
    error = false,
    disabled = false,
    fullWidth = false,
    required = false,
    color,
    margin = 'none',
    size: formControlSize,
    variant: formControlVariant = 'outlined',
    inputLabelProps,
    sx: boxSx,
    radioGroupSx,
    ...restRadio
  } = props;

  const { row = false, id: idProp, name, ...restRadioGroup } = restRadio;
  const { sx: inputLabelUserSx, ...inputLabelRest } = inputLabelProps ?? {};
  const generatedId = useId();

  const helperPresent = hasRenderableHelperText(helperText);
  const ariaIds = useMemo(() => buildRadioGroupAriaIds(idProp, generatedId), [idProp, generatedId]);

  const outlinedBoxSx = useMemo(
    () => mergeRadioGroupBorderSx(getRadioGroupOutlinedBoxSx({ error, disabled }), boxSx),
    [boxSx, error, disabled],
  );

  const radioGroupLayoutSx = useMemo(
    () =>
      buildRadioGroupLayoutSx({
        row,
        radioGroupSx,
        formControlLabelRowSx: FORM_CONTROL_LABEL_ROW_SX,
        formControlLabelVerticalSx: FORM_CONTROL_LABEL_VERTICAL_SX,
      }),
    [row, radioGroupSx],
  );

  return (
    <FormControl
      error={error}
      disabled={disabled}
      fullWidth={fullWidth}
      required={required}
      color={color}
      margin={margin}
      size={formControlSize}
      variant={formControlVariant}
      component="div"
    >
      <BaseFieldContainer
        required={required}
        disabled={disabled}
        error={error}
        fullWidth={fullWidth}
        alwaysRenderContainer
      >
        <InputLabel
          {...inputLabelRest}
          id={ariaIds.labelId}
          error={error}
          disabled={disabled}
          required={false}
          shrink
          sx={buildInputLabelSx(inputLabelUserSx)}
        >
          {label}
        </InputLabel>
        <Box sx={outlinedBoxSx}>
          <RadioGroup
            ref={ref}
            id={idProp}
            name={name}
            aria-labelledby={label ? ariaIds.labelId : undefined}
            aria-describedby={helperPresent ? ariaIds.helperTextId : undefined}
            row={row}
            {...restRadioGroup}
            sx={radioGroupLayoutSx}
          >
            {options != null
              ? options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size={radioSize} />}
                    label={option.label}
                  />
                ))
              : children}
          </RadioGroup>
        </Box>
      </BaseFieldContainer>
      {helperPresent ? (
        <FormHelperText
          id={ariaIds.helperTextId}
          error={error}
          disabled={disabled}
          component="p"
          sx={{ margin: 0, textAlign: 'start', width: '100%' }}
        >
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
});

export default FRadioGroup;
